import { useTranslation } from "react-i18next";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@nexus/ui";
import type { ChangelogNotesAudience } from "@/features/changelog/api/changelog.api";
import { NumberField, SettingRow, SettingSwitch, SettingsCard } from "../shared/SettingsCard";
import { AUDIENCES, LIMITS } from "./changelog-settings-limits";
import type { ChangelogSettingsCardProps } from "./types";

/** How the generated notes read, applied next time notes are written. */
export function ReleaseNotesCard({ draft, set, isReadOnly }: ChangelogSettingsCardProps) {
  const { t } = useTranslation();

  return (
    <SettingsCard
      icon="sparkles"
      title={t("admin.changelog.settings.notes.title", "Release notes")}
      description={t(
        "admin.changelog.settings.notes.description",
        "How the generated notes read. Changes here apply the next time notes are written or regenerated.",
      )}
    >
      <SettingRow
        label={t("admin.changelog.settings.notesAudience", "Write notes for")}
        description={t(
          "admin.changelog.settings.notesAudienceHelp",
          "Who the notes are addressed to, which decides whether a refactor is worth a line.",
        )}
        control={
          <Select
            value={draft.notesAudience}
            disabled={isReadOnly}
            onValueChange={(value) => set("notesAudience", value as ChangelogNotesAudience)}
          >
            <SelectTrigger className="h-8 w-44 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {AUDIENCES.map((audience) => (
                <SelectItem key={audience} value={audience} className="text-xs">
                  {t(`admin.changelog.settings.audienceOption.${audience}`, audience)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        }
      />

      <SettingRow
        htmlFor="include-jira"
        label={t("admin.changelog.settings.includeJiraLinks", "Mention ticket keys")}
        description={t(
          "admin.changelog.settings.includeJiraLinksHelp",
          "Useful in Slack, noise in a customer email. Off strips them from the notes entirely.",
        )}
        control={
          <SettingSwitch
            id="include-jira"
            checked={draft.includeJiraLinks}
            disabled={isReadOnly}
            onCheckedChange={(checked) => set("includeJiraLinks", checked)}
          />
        }
      />

      <SettingRow
        htmlFor="max-items"
        label={t("admin.changelog.settings.maxItems", "At most")}
        description={t(
          "admin.changelog.settings.maxItemsHelp",
          "Per section. A release with forty fixes gets the first few, not all of them.",
        )}
        control={
          <NumberField
            id="max-items"
            value={draft.maxItemsPerSection}
            min={LIMITS.maxItemsPerSection.min}
            max={LIMITS.maxItemsPerSection.max}
            unit={t("admin.changelog.settings.items", "items")}
            disabled={isReadOnly}
            onChange={(value) => set("maxItemsPerSection", value)}
          />
        }
      />
    </SettingsCard>
  );
}
